/**
 * Health Container Module — Whoop-powered health dashboard.
 * Sparkline chart + metric cards with color-coded sources.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import {
  listIntegrations,
  createIntegration,
  authorizeIntegration,
  deleteIntegration,
} from '@/lib/api/endpoints/integrations';
import {
  listModuleSidebarPreferences,
  updateModuleSidebarPreference,
} from '@/lib/api/endpoints/workspaces';
import MetricsSettingsPanel from './MetricsSettingsPanel';
import { http } from '@/lib/http';
import { useToast } from '@/app/providers/ToastContext';
import { openChatStream } from '@/lib/api/endpoints/chat';
import type { Module } from '@/types/modules';
import HealthProfile from './HealthProfile';
import DailyLogView from './DailyLogView';
import AddMealModal from './AddMealModal';
import LabResultsView from './LabResultsView';
import { ModuleSearchBar } from '@/common/components/ModuleSearchBar';
import { MobileSearchToggle } from '@/common/components/MobileSearchToggle';
import { MobileActionButton } from '@/common/components/MobileActionButton';
import { ScrollableNav } from '@/common/components/ScrollableNav';
import { useIsCompactLayout } from '@/common/hooks';
import { ModuleEmptyState } from '../_infrastructure/ModuleEmptyState';
import {
  PROVIDER_COLORS,
  SOURCE_LABELS,
  PROVIDER_DEFAULT_STARS,
  WEARABLE_OPTIONS,
} from './constants';
import { metricOf, providerOf } from './utils/metric-keys';
import { getSourceColor } from './utils/source-color';
import { Sparkline } from './components/Sparkline';


/** Check if a key matches any entry in a preferences list, handling legacy unprefixed keys.
 *  - "oura/restingHeartRate" matches pref "oura/restingHeartRate" (exact)
 *  - "oura/restingHeartRate" matches legacy pref "restingHeartRate" ONLY if no other provider's
 *    version of that metric is explicitly in prefs (prevents one legacy pref matching all providers)
 *  - Unprefixed keys match as-is */
function prefsMatch(prefs: string[], key: string): boolean {
  if (prefs.includes(key)) return true;
  const m = metricOf(key);
  const prov = providerOf(key);
  // Only fall back to unprefixed match if the key has no provider, OR
  // the prefs list has the unprefixed version but no provider-prefixed versions of this metric
  if (!prov) return prefs.includes(m);
  if (prefs.includes(m) && !prefs.some(p => p.includes('/') && metricOf(p) === m)) return true;
  return false;
}




interface HealthContainerProps {
  module: Module;
  conversationId: string;
}


export default function HealthContainerModule({ module, conversationId }: HealthContainerProps) {
  const { token, selectedContainer } = useSession();
  const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isMobile = useIsCompactLayout();
  const [connecting, setConnecting] = useState<string | false>(false);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTabState] = useState<'log' | 'trends' | 'profile' | 'labs'>(() => {
    const saved = localStorage.getItem('health-active-tab');
    return (saved === 'log' || saved === 'trends' || saved === 'profile' || saved === 'labs') ? saved : 'log';
  });
  const setActiveTab = (tab: 'log' | 'trends' | 'profile' | 'labs') => {
    setActiveTabState(tab);
    localStorage.setItem('health-active-tab', tab);
  };

  const handleSilentSendMessage = useCallback(async (message: string, images?: Array<{ mediaType: string; data: string; filename: string }>) => {
    const isLabs = activeTab === 'labs';
    const phrases = ['Working on it...', 'Crunching the numbers...', 'On it...', 'Logging that...', 'One moment...', 'Almost there...'];
    showToast(isLabs ? 'Reading lab report...' : phrases[Math.floor(Math.random() * phrases.length)], 'info');
    try {
      // Call the agent directly via SSE — bypass ChatContext to avoid provider dependency
      // skipHistory prevents the LLM from seeing prior food-logging messages and re-logging them
      for await (const event of openChatStream(token ?? null, {
        messages: [{ role: 'user', content: message }],
        conversationId,
        workspaceId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        imageAttachments: images,
        activeModuleId: module.id,
        activeModuleObjectType: 'health',
        activeModuleLabel: 'Health',
        skipHistory: true,
      })) {
        // Consume stream — check for errors (event.type is the SSE event type)
        if (event.type === 'error' || (event.data as any)?.type === 'error') {
          throw new Error((event.data as any).error || (event.data as any)?.message || 'Agent error');
        }
      }
      showToast(isLabs ? 'Lab report added' : 'Entry logged', 'success');
      queryClient.invalidateQueries({ queryKey: ['health-profile'] });
      // Force immediate data refresh by bumping refetchTick
      setRefetchTick(t => t + 1);
      // Backup refetch after short delay for DB write lag
      setTimeout(() => setRefetchTick(t => t + 1), 1500);
    } catch (err) {
      showToast(isLabs ? 'Failed to process lab report' : 'Failed to log entry', 'error');
      throw err;
    }
  }, [token, conversationId, workspaceId, showToast, queryClient, activeTab]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const handleAddEntry = useCallback(async (message: string, photo?: File) => {
    if (!message.trim() && !photo) return;
    setAddSubmitting(true);
    const isLabs = activeTab === 'labs';
    try {
      let images: Array<{ mediaType: string; data: string; filename: string }> | undefined;
      if (photo) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(photo);
        });
        images = [{ mediaType: photo.type || 'image/jpeg', data: base64, filename: photo.name }];
      }
      if (isLabs && !photo) {
        // For text-based lab results, create the stat directly via API
        // Parse raw text into a simple lab report stat
        const today = new Date().toISOString().slice(0, 10);
        // Try to extract a date from the text
        const dateMatch = message.match(/(\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+\d{1,2},?\s+\d{4})/i);
        const reportDate = dateMatch ? new Date(dateMatch[1]).toISOString().slice(0, 10) : today;

        // Parse lab values — normalize tabs to newlines, handle all formats
        const normalized = message.replace(/\t/g, '\n'); // Tabs to newlines
        const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean);
        const labResults: Array<{ test: string; value: string | number; unit: string; referenceRange: string; flag: string }> = [];

        // Skip known non-test lines
        const skipPatterns = /^(Your Value|Reference Range|See More|Notes:|Blog|Media|About|Careers|Contact|FAQs|Membership|Medical|Privacy|Practice|Sponsored|Hi |Please|We invite|If your|If you|Take care|The One|CMP|Lipids|CBC|Hemoglobin A1c$)/i;

        const getFlag = (val: number, refStr: string): string => {
          const r = refStr.replace(/[–—]/g, '-').match(/([\d.]+)-([\d.]+)/);
          if (r) return val < parseFloat(r[1]) ? 'low' : val > parseFloat(r[2]) ? 'high' : 'normal';
          if (refStr.match(/^>/)) return val < parseFloat(refStr.slice(1)) ? 'low' : 'normal';
          if (refStr.match(/^</)) return val > parseFloat(refStr.slice(1)) ? 'high' : 'normal';
          return 'normal';
        };

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (skipPatterns.test(line)) continue;

          // Check: is this a test name followed by a numeric value line?
          const isName = /^[A-Za-z(]/.test(line) && !/^[\d.]/.test(line);
          const next = lines[i + 1] || '';
          const next2 = lines[i + 2] || '';
          const valMatch = next.match(/^([\d.]+)\s+(.*)/);

          if (isName && valMatch) {
            const val = parseFloat(valMatch[1]);
            const unit = valMatch[2].trim();
            // Check if next2 is a reference range
            const refMatch = next2.match(/^([<>]?[\d.]+[–\-][\d.]+)\s*(.*)/);
            const refStr = refMatch ? refMatch[1].replace(/[–—]/g, '-') + (refMatch[2] ? ' ' + refMatch[2] : '') : '';
            labResults.push({ test: line, value: val, unit, referenceRange: refStr.trim(), flag: getFlag(val, refStr) });
            i += refMatch ? 2 : 1;
            continue;
          }

          // Single-line: "Glucose  84.0 mg/dL  70-99 mg/dL"
          const sl = line.match(/^(.+?)\s+([\d.]+)\s+(\S+)\s+([\d.<>]+[–\-][\d.<>]+\s*\S*)/);
          if (sl) {
            const val = parseFloat(sl[2]);
            const refStr = sl[4].replace(/[–—]/g, '-');
            labResults.push({ test: sl[1].trim(), value: val, unit: sl[3], referenceRange: refStr, flag: getFlag(val, refStr) });
          }
        }

        // Create stat directly via API
        await api.objects.createObject(token ?? null, {
          type: 'stat',
          label: `Lab Results — ${reportDate}`,
          value: labResults.length,
          unit: 'tests',
          date: `${reportDate}T00:00:00.000Z`,
          source: 'health:labs',
          category: 'labs',
          labResults,
          panelName: 'Lab Report',
          moduleLinks: [{ moduleId: module.id }],
        } as any);
        queryClient.invalidateQueries({ queryKey: [...queryKeys.objects.byType('stat'), 'health'] });
        showToast('Lab report added', 'success');
      } else {
        let prompt: string;
        if (isLabs && photo) {
          prompt = 'Read this lab report image and log all the test results with values, units, and reference ranges using log_labs. Use the date shown on the report, not today.';
        } else if (activeTab === 'profile') {
          prompt = `Update my health profile: ${message.trim()}`;
        } else {
          prompt = photo && !message.trim() ? 'Identify and log the food in this photo' : `Log this food: ${message.trim()}`;
        }
        await handleSilentSendMessage(prompt, images);
      }
      setShowAddModal(false);
    } catch (err) {
      console.error('[HealthAddEntry] Error:', err);
      showToast('Failed to save', 'error');
    } finally {
      setAddSubmitting(false);
    }
  }, [handleSilentSendMessage, activeTab, token, module.id, queryClient, showToast]);

  const [showObjectives, setShowObjectives] = useState(false);
  const [objectiveCalories, setObjectiveCalories] = useState('');
  const [objectiveProtein, setObjectiveProtein] = useState('');

  // Track whether a menu-triggered sync is pending (set by event, consumed by effect)
  const [pendingMenuSync, setPendingMenuSync] = useState(false);

  // Listen for menu events from ModuleRenderer
  useEffect(() => {
    const handleSetObjectives = () => setShowObjectives(true);
    const handleMenuSync = () => setPendingMenuSync(true);
    const handleManageWearables = () => setShowSwitchModal(true);
    window.addEventListener('xavior:health-set-objectives', handleSetObjectives);
    window.addEventListener('xavior:health-sync-wearable', handleMenuSync);
    window.addEventListener('xavior:health-manage-wearables', handleManageWearables);
    return () => {
      window.removeEventListener('xavior:health-set-objectives', handleSetObjectives);
      window.removeEventListener('xavior:health-sync-wearable', handleMenuSync);
      window.removeEventListener('xavior:health-manage-wearables', handleManageWearables);
    };
  }, []);

  const handleSaveObjectives = useCallback(async () => {
    const cal = parseInt(objectiveCalories) || 0;
    const prot = parseInt(objectiveProtein) || 0;
    if (cal > 0 || prot > 0) {
      const fields: string[] = [];
      if (cal > 0) fields.push(`daily calorie target: ${cal}`);
      if (prot > 0) fields.push(`daily protein target: ${prot}g`);
      await handleSilentSendMessage(`Update my health profile: ${fields.join(', ')}`);
    }
    setShowObjectives(false);
  }, [objectiveCalories, objectiveProtein, handleSilentSendMessage]);

  const handleAIObjectives = useCallback(async () => {
    await handleSilentSendMessage('Based on my health profile (age, weight, height, goals), recommend daily calorie and protein targets and update my profile with them.');
    setShowObjectives(false);
  }, [handleSilentSendMessage]);

  const HEALTH_PROVIDERS = ['oura', 'whoop', 'strava', 'fitbit'] as const;

  const { data: activeWearable } = useQuery({
    queryKey: ['integrations', 'health-wearable'],
    queryFn: async () => {
      const integrations = await listIntegrations(token ?? null);
      for (const provider of HEALTH_PROVIDERS) {
        const found = integrations.find((i: any) => i.provider === provider && i.status === 'active');
        if (found) return { ...found, provider } as any;
      }
      return null;
    },
    enabled: Boolean(token),
  });
  const [showSwitchModal, setShowSwitchModal] = useState(false);

  const handleConnectProvider = useCallback(async (provider: string, label: string) => {
    if (!token) return;
    setConnecting(provider);
    try {
      const integration = await createIntegration(token, { provider, name: `My ${label}` });
      const authUrl = await authorizeIntegration(token, integration.id, window.location.href);
      window.open(authUrl, '_blank', 'noopener');
      // Re-check wearable status when user returns from OAuth tab
      const onFocus = () => {
        queryClient.invalidateQueries({ queryKey: ['integrations', 'health-wearable'] });
        window.removeEventListener('focus', onFocus);
      };
      window.addEventListener('focus', onFocus);
    } catch (err) {
      console.error(`Failed to connect ${label}:`, err);
      showToast(`Failed to connect ${label}. Please try again.`, 'error');
    }
    finally { setConnecting(false); }
  }, [token, showToast, queryClient]);

  const handleDisconnectWearable = useCallback(async () => {
    if (!token || !activeWearable) return;
    try {
      await deleteIntegration(token, activeWearable.id);
      queryClient.invalidateQueries({ queryKey: ['integrations', 'health-wearable'] });
      showToast('Wearable disconnected.', 'success');
    } catch (err) {
      console.error('Failed to disconnect wearable:', err);
      showToast('Failed to disconnect wearable.', 'error');
    }
  }, [token, activeWearable, queryClient, showToast]);


  const renderWearableButtons = (onSkip?: () => void) => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, width: '100%', maxWidth: 300, margin: '8px auto 0' }}>
        {WEARABLE_OPTIONS.map((w) => {
          const isConnecting = connecting === w.provider;
          return (
            <button
              key={w.provider}
              type="button"
              disabled={!!connecting}
              onClick={() => handleConnectProvider(w.provider, w.name)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, border: '1px solid var(--color-border, #DADCE0)',
                background: 'transparent', cursor: connecting ? 'wait' : 'pointer',
                fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
                opacity: (!!connecting && !isConnecting) ? 0.5 : 1,
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: 7, background: w.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {w.logo}
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{w.name}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{w.desc}</div>
              </div>
              {isConnecting && (
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Connecting...</span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <button
          type="button"
          className="module-empty-state__secondary"
          onClick={onSkip ?? (() => {})}
        >
          Use health tracking without a wearable...
        </button>
      </div>
    </>
  );

  const handleSyncWearable = useCallback(async () => {
    if (!token || !activeWearable) return;
    setSyncing(true);
    try {
      const integrationId = activeWearable.id;
      if (activeWearable.provider === 'whoop') {
        await http.post(`/integrations/${integrationId}/whoop-sync`, { days: 14 }, { authToken: token });
      } else {
        await http.post(`/integrations/${integrationId}/sync`, {
          ...(conversationId ? { conversationId } : {}),
          module_id: module.id,
        }, { authToken: token });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('stat') });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.objects.byType('stat'), 'health'] });
      showToast('Sync complete', 'success');
    } catch (err: any) {
      const message = err?.body?.error?.message || err?.message || '';
      console.error('Health sync failed:', err);
      if (message.includes('rate limit') || message.includes('wait')) {
        showToast('Please wait 30 seconds between syncs.', 'info');
      } else {
        showToast('Sync failed. Please try again.', 'error');
      }
    }
    finally { setSyncing(false); }
  }, [token, activeWearable, conversationId, module.id, queryClient, showToast]);

  // Trigger sync when requested via menu event
  useEffect(() => {
    if (pendingMenuSync) {
      setPendingMenuSync(false);
      handleSyncWearable();
    }
  }, [pendingMenuSync, handleSyncWearable]);

  // Force refetch when agent logs health data (dispatched from ChatBox on tool completion)
  const [refetchTick, setRefetchTick] = useState(0);
  useEffect(() => {
    const handler = () => {
      // Small delay to let the API commit the write
      setTimeout(() => setRefetchTick(t => t + 1), 500);
    };
    window.addEventListener('health-navigate-date', handler);
    return () => window.removeEventListener('health-navigate-date', handler);
  }, []);

  const { data: healthStats = [] } = useQuery({
    queryKey: [...queryKeys.objects.byType('stat'), 'health', refetchTick],
    queryFn: async () => {
      const result = await api.objects.getObjectsByType(token ?? null, 'stat', { limit: 1000 });
      const stats = result?.items || [];
      return stats.filter((s: any) => {
        const attrs = s.attributes || {};
        const source = attrs.source || s.source || '';
        const category = attrs.category || s.category || '';
        const integrationId = attrs.integrationId || s.integrationId || '';
        if (source === 'health:profile') return false; // Profile shown separately
        // Include: wearable sources, health category, manual entries, and any integration-synced stats
        const HEALTH_CATEGORIES = new Set(['health', 'sleep', 'readiness', 'activity', 'heart', 'recovery', 'strain', 'workout', 'body', 'stress']);
        const WEARABLE_PROVIDERS = ['whoop', 'oura', 'fitbit', 'strava'];
        return WEARABLE_PROVIDERS.some(p => source.startsWith(p + ':')) || source.startsWith('health:') || HEALTH_CATEGORIES.has(category) || source === 'manual';
      });
    },
    enabled: Boolean(token && workspaceId),
    staleTime: 0,
    refetchInterval: 3_000,
  });

  const [showMetricsSettings, setShowMetricsSettings] = useState(false);
  const gearRef = useRef<HTMLButtonElement>(null);

  // Load metric preferences from module_user_preferences
  const { data: metricsPrefs } = useQuery({
    queryKey: ['module-preferences', workspaceId, module.id, 'metrics-settings'],
    queryFn: async () => {
      if (!token || !workspaceId) return null;
      const prefs = await listModuleSidebarPreferences(token, workspaceId);
      const modulePref = prefs.find((p: any) => p.moduleId === module.id);
      return modulePref?.settings ?? null;
    },
    enabled: Boolean(token && workspaceId),
    staleTime: 60_000,
  });

  // Optimistic local state — initialized from server, updated immediately on toggle
  const providerDefaults = activeWearable ? PROVIDER_DEFAULT_STARS[activeWearable.provider] ?? [] : [];
  const [localStarred, setLocalStarred] = useState<string[] | null>(null);
  const [localHidden, setLocalHidden] = useState<string[] | null>(null);

  // Sync from server on first load
  useEffect(() => {
    if (metricsPrefs && localStarred === null) {
      setLocalStarred(metricsPrefs.starredMetrics ?? null);
      setLocalHidden(metricsPrefs.hiddenMetrics ?? null);
    }
  }, [metricsPrefs, localStarred]);

  const starredMetrics: string[] = localStarred ?? metricsPrefs?.starredMetrics ?? providerDefaults;
  const hiddenMetrics: string[] = localHidden ?? metricsPrefs?.hiddenMetrics ?? [];

  const handleMetricsUpdate = useCallback(async (newStarred: string[], newHidden: string[]) => {
    // Update local state immediately for instant UI feedback
    setLocalStarred(newStarred);
    setLocalHidden(newHidden);
    // Persist to API in background
    if (!token || !workspaceId) return;
    try {
      await updateModuleSidebarPreference(token, workspaceId, module.id, {
        settings: { starredMetrics: newStarred, hiddenMetrics: newHidden },
      });
      queryClient.invalidateQueries({ queryKey: ['module-preferences', workspaceId, module.id, 'metrics-settings'] });
    } catch (err) {
      console.error('Failed to save metric preferences:', err);
    }
  }, [token, workspaceId, module.id, queryClient]);

  // Deduplicate, sort, and filter by search query
  const allItems = useMemo(() => {
    const seen = new Set<string>();
    const deduped = healthStats
      .filter((item: any) => { if (seen.has(item.id)) return false; seen.add(item.id); return true; })
      .sort((a: any, b: any) => {
        const dateA = a.date || a.attributes?.date || a.createdAt || '';
        const dateB = b.date || b.attributes?.date || b.createdAt || '';
        return dateB.localeCompare(dateA);
      });
    if (!searchQuery.trim()) return deduped;
    const q = searchQuery.toLowerCase();
    return deduped.filter((item: any) => {
      const label = (item.title || item.label || '').toLowerCase();
      const source = (item.source || item.attributes?.source || '').toLowerCase();
      const meal = (item.meal || item.attributes?.meal || '').toLowerCase();
      const itemNames = (item.items || []).map((i: any) => (i.name || '').toLowerCase()).join(' ');
      return label.includes(q) || source.includes(q) || meal.includes(q) || itemNames.includes(q);
    });
  }, [healthStats, searchQuery]);

  // Group by date — show wearable data (Oura, Whoop, Fitbit, Strava), skip food/exercise "Untitled" items
  const grouped = useMemo(() => {
    const WEARABLE_PROVIDERS = ['whoop', 'oura', 'fitbit', 'strava'];
    const WEARABLE_CATEGORIES = new Set(['sleep', 'readiness', 'activity', 'heart', 'recovery', 'strain', 'workout', 'body', 'stress']);
    const wearableItems = allItems.filter((item: any) => {
      const source = item.source || item.attributes?.source || '';
      const integrationId = item.integrationId || item.attributes?.integrationId || '';
      const category = item.category || item.attributes?.category || '';
      return WEARABLE_PROVIDERS.some(p => source.startsWith(p + ':')) || WEARABLE_CATEGORIES.has(category);
    });
    return wearableItems.reduce((acc: Record<string, any[]>, item: any) => {
      const date = (item.date || item.attributes?.date || item.createdAt || '').slice(0, 10);
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    }, {});
  }, [allItems]);

  // Sparkline data — group by source for chart
  // Trends: show weight, calories, exercise, BP + Whoop metrics
  const chartData = useMemo(() => {
    const bySource: Record<string, { date: string; value: number }[]> = {};
    const caloriesByDate: Record<string, number> = {};
    const proteinByDate: Record<string, number> = {};
    const burnedByDate: Record<string, number> = {};

    for (const item of allItems) {
      let source = item.source || item.attributes?.source || '';
      // Synthesize source for integration-synced items missing the source field (legacy data)
      if (!source && (item.integrationId || item.attributes?.integrationId)) {
        const category = item.category || item.attributes?.category || 'data';
        const endpointId = item.endpointId || item.attributes?.endpointId || '';
        // Detect provider from endpointId patterns or active wearable
        const provider = activeWearable?.provider || (endpointId.includes('daily_') || endpointId.includes('heart_rate') || endpointId.includes('spo2') ? 'oura' : 'wearable');
        source = `${provider}:${category}`;
      }
      const date = (item.date || item.attributes?.date || '').slice(0, 10);
      const value = item.value ?? item.attributes?.value;
      if (!source || !date) continue;

      // Aggregate daily calories from food entries
      if (source === 'health:food') {
        const cal = item.totalCalories || item.attributes?.totalCalories || 0;
        const prot = item.totalProtein || item.attributes?.totalProtein || 0;
        caloriesByDate[date] = (caloriesByDate[date] || 0) + cal;
        proteinByDate[date] = (proteinByDate[date] || 0) + prot;
        continue;
      }

      // Aggregate daily exercise burned
      if (source === 'health:exercise') {
        const burned = item.totalCaloriesBurned || item.attributes?.totalCaloriesBurned || 0;
        burnedByDate[date] = (burnedByDate[date] || 0) + burned;
        continue;
      }

      // Weight and BP — direct values
      if (source === 'health:vitals') {
        const label = (item.label || item.attributes?.label || '').toLowerCase();
        if (label.includes('weight') && value) {
          if (!bySource['weight']) bySource['weight'] = [];
          bySource['weight'].push({ date, value: Number(value) });
        }
        if (label.includes('blood') || item.systolic || item.attributes?.systolic) {
          const sys = item.systolic || item.attributes?.systolic;
          const dia = item.diastolic || item.attributes?.diastolic;
          if (sys) {
            if (!bySource['systolic']) bySource['systolic'] = [];
            bySource['systolic'].push({ date, value: Number(sys) });
          }
          if (dia) {
            if (!bySource['diastolic']) bySource['diastolic'] = [];
            bySource['diastolic'].push({ date, value: Number(dia) });
          }
        }
        continue;
      }

      // Wearable and other sources (skip labs — not chartable)
      if (source === 'health:labs') continue;
      if (value !== undefined) {
        // For wearable data, group by metric name (not source category) so each metric
        // gets its own chart line. E.g. readinessScore vs temperatureDeviation instead
        // of all being lumped under "oura:readiness".
        // Prefix with provider (e.g. "oura/restingHeartRate") so overlapping metrics
        // from different wearables are distinguishable and independently togglable.
        const metric = item.title || item.label || item.attributes?.label || '';
        const WEARABLE_PREFIXES = ['oura:', 'fitbit:', 'strava:'];
        const isWearable = WEARABLE_PREFIXES.some(p => source.startsWith(p)) || source.startsWith('whoop:');
        const provider = isWearable ? source.split(':')[0] : '';
        const chartKey = isWearable && metric ? (provider ? `${provider}/${metric}` : metric) : source;
        if (!bySource[chartKey]) bySource[chartKey] = [];
        bySource[chartKey].push({ date, value: Number(value) });
      }
    }

    // Convert aggregated daily totals to chart series
    for (const [date, cal] of Object.entries(caloriesByDate)) {
      if (!bySource['calories']) bySource['calories'] = [];
      bySource['calories'].push({ date, value: cal });
    }
    for (const [date, prot] of Object.entries(proteinByDate)) {
      if (prot > 0) {
        if (!bySource['protein']) bySource['protein'] = [];
        bySource['protein'].push({ date, value: prot });
      }
    }
    for (const [date, burned] of Object.entries(burnedByDate)) {
      if (!bySource['exercise']) bySource['exercise'] = [];
      bySource['exercise'].push({ date, value: burned });
    }

    return bySource;
  }, [allItems]);

  // Summary stats — 30-day averages, primary scores first
  // Primary metrics shown as large cards; secondary metrics shown as smaller rows
  const PRIMARY_METRICS = new Set([
    // Oura
    'readinessScore', 'sleepScore', 'activityScore', 'heartRate', 'spo2Average', 'steps', 'stressHigh',
    // Whoop
    'whoop:strain', 'whoop:recovery', 'whoop:sleep',
    // Manual
    'calories', 'protein', 'exercise', 'weight', 'systolic', 'diastolic',
  ]);
  const METRIC_UNITS: Record<string, string> = {
    readinessScore: '', sleepScore: '', activityScore: '', heartRate: 'bpm', spo2Average: '%',
    steps: 'steps', activeCalories: 'cal', totalCalories: 'cal', equivalentWalkingDistance: 'm',
    stressHigh: 'min', recoveryHigh: 'min',
    sleepDuration: 'hrs', sleepDurationMs: 'hrs', sleepDeepDuration: 'hrs', sleepRemDuration: 'hrs', sleepLightDuration: 'hrs',
    sleepAwakeTime: 'min', sleepEfficiencyPct: '%', sleepAverageHRV: 'ms', sleepAverageHR: 'bpm',
    sleepLowestHR: 'bpm', sleepAverageBreathRate: '/min', sleepRestlessPeriods: '',
    deepSleep: '', remSleep: '', sleepLatency: '', sleepRestfulness: '', sleepTiming: '', totalSleep: '',
    temperatureDeviation: '°C', temperatureTrendDeviation: '°C',
    highActivityTime: 'min', mediumActivityTime: 'min', lowActivityTime: 'min',
    sedentaryTime: 'min', restingTime: 'min',
    calories: 'cal/day', protein: 'g/day', exercise: 'cal/day', weight: 'lbs',
    systolic: 'mmHg', diastolic: 'mmHg',
  };
  // Convert time-based metrics to display-friendly values
  const SEC_TO_MIN_METRICS = new Set(['stressHigh', 'recoveryHigh', 'sleepAwakeTime', 'highActivityTime', 'mediumActivityTime', 'lowActivityTime', 'sedentaryTime', 'restingTime']);
  const SEC_TO_HRS_METRICS = new Set(['sleepDuration', 'sleepDeepDuration', 'sleepRemDuration', 'sleepLightDuration']);
  const MS_TO_HRS_METRICS = new Set(['sleepDurationMs']);

  const summaryCards = useMemo(() => {
    const cards: { source: string; label: string; provider: string; latest: number; unit: string; trend: number; color: ReturnType<typeof getSourceColor>; isPrimary: boolean }[] = [];
    for (const [key, points] of Object.entries(chartData)) {
      if (points.length < 2) continue;
      let avg = points.reduce((s, p) => s + p.value, 0) / points.length;
      // Convert units for display
      const m = metricOf(key);
      if (SEC_TO_MIN_METRICS.has(m)) avg = avg / 60;
      if (SEC_TO_HRS_METRICS.has(m)) avg = avg / 3600;
      if (MS_TO_HRS_METRICS.has(m)) avg = avg / 3_600_000;
      avg = Math.round(avg * 10) / 10; // 1 decimal
      if (Number.isInteger(avg)) avg = Math.round(avg);
      // Trend: compare last 7 days avg vs overall
      const sorted = [...points].sort((a, b) => b.date.localeCompare(a.date));
      const recent7 = sorted.slice(0, 7);
      let recentAvg = recent7.length > 0 ? recent7.reduce((s, p) => s + p.value, 0) / recent7.length : avg;
      if (SEC_TO_MIN_METRICS.has(m)) recentAvg = recentAvg / 60;
      if (SEC_TO_HRS_METRICS.has(m)) recentAvg = recentAvg / 3600;
      if (MS_TO_HRS_METRICS.has(m)) recentAvg = recentAvg / 3_600_000;
      recentAvg = Math.round(recentAvg * 10) / 10;
      const trend = Math.round((recentAvg - avg) * 10) / 10;
      const unit = METRIC_UNITS[m] || METRIC_UNITS[key] || '';
      const isPrimary = PRIMARY_METRICS.has(m) || PRIMARY_METRICS.has(key);
      cards.push({
        source: key,
        label: SOURCE_LABELS[m] || SOURCE_LABELS[key] || m.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
        provider: providerOf(key),
        latest: avg,
        unit,
        trend,
        color: getSourceColor(key),
        isPrimary,
      });
    }
    // Sort: primary metrics first, then alphabetical
    cards.sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
    return cards;
  }, [chartData]);

  const hasData = Object.keys(grouped).length > 0 || Object.keys(chartData).length > 0;

  const [consentDismissed, setConsentDismissed] = useState(() =>
    localStorage.getItem('health-consent-dismissed') === 'true'
  );
  const dismissConsent = () => {
    setConsentDismissed(true);
    localStorage.setItem('health-consent-dismissed', 'true');
  };

  const [wearableSkipped, setWearableSkipped] = useState(() =>
    localStorage.getItem('health-wearable-skipped') === 'true'
  );
  const skipWearable = useCallback(() => {
    setWearableSkipped(true);
    localStorage.setItem('health-wearable-skipped', 'true');
  }, []);

  return (
    <div className="module module-health" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Tab bar — always visible so users can navigate even from welcome */}
      <div className="module-nav">
        <div className="module-nav__primary">
          <div className="module-toolbar-button-group">
            <button className={`module-toolbar-button health-tab ${activeTab === 'log' ? 'is-active' : ''}`} onClick={() => setActiveTab('log')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span className="module-toolbar-label" data-text="Log">Log</span>
            </button>
            <button className={`module-toolbar-button health-tab ${activeTab === 'trends' ? 'is-active' : ''}`} onClick={() => setActiveTab('trends')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              <span className="module-toolbar-label" data-text="Trends">Trends</span>
            </button>
            <button className={`module-toolbar-button health-tab ${activeTab === 'labs' ? 'is-active' : ''}`} onClick={() => setActiveTab('labs')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v11l4.72 8.26a1 1 0 0 1-.87 1.49H5.15a1 1 0 0 1-.87-1.49L9 14z"/></svg>
              <span className="module-toolbar-label" data-text="Labs">Labs</span>
            </button>
            <button className={`module-toolbar-button health-tab ${activeTab === 'profile' ? 'is-active' : ''}`} onClick={() => setActiveTab('profile')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span className="module-toolbar-label" data-text="Profile">Profile</span>
            </button>
          </div>
        </div>
        <div className="module-nav__secondary" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <MobileSearchToggle targetId="health-search-toolbar" />
          <MobileActionButton onClick={() => setShowAddModal(true)} title={activeTab === 'labs' ? 'Add labs' : activeTab === 'profile' ? 'Add to profile' : 'Log'} />
          {!isMobile && activeWearable && (
            <button
              type="button"
              className="module-toolbar-button"
              onClick={handleSyncWearable}
              disabled={syncing}
              title="Sync wearable data"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: syncing ? 'health-spin 1s linear infinite' : undefined }}>
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              <span className="module-toolbar-label">{syncing ? 'Syncing' : 'Sync'}</span>
            </button>
          )}
          {!isMobile && !activeWearable && (
            <button
              type="button"
              className="module-toolbar-button"
              onClick={() => setShowSwitchModal(true)}
              title="Connect wearable"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="7" ry="7" />
                <line x1="12" y1="18" x2="12" y2="18.01" />
              </svg>
              <span className="module-toolbar-label">Connect</span>
            </button>
          )}
        </div>
      </div>

      <div id="health-search-toolbar" className="module-toolbar module-search-row">
        <ModuleSearchBar standalone={false} value={searchQuery} onChange={setSearchQuery} onClose={() => setSearchQuery('')} placeholder="Search health data..." resultCount={searchQuery ? allItems.length : undefined} autoFocus={false} />
        {!isMobile && activeTab === 'log' && (
          <button type="button" className="module-toolbar-button is-primary" onClick={() => setShowAddModal(true)}>+ Log</button>
        )}
        {!isMobile && activeTab === 'labs' && (
          <button type="button" className="module-toolbar-button is-primary" onClick={() => setShowAddModal(true)}>+ Labs</button>
        )}
        {!isMobile && activeTab === 'profile' && (
          <button type="button" className="module-toolbar-button is-primary" onClick={() => setShowAddModal(true)}>+ Profile</button>
        )}
      </div>

      {!consentDismissed && (
        <div style={{
          margin: '0 16px', padding: '10px 12px', borderRadius: 8,
          background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.12)',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Your health data is stored privately in your workspace and is not shared with anyone. You control what you add and can delete it at any time.
          </div>
          <button
            onClick={dismissConsent}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
              color: 'var(--color-text-tertiary)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
        {activeTab === 'log' && (!activeWearable && allItems.length === 0 && !wearableSkipped ? (
          <>
            <ModuleEmptyState
              icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
              iconColor="#ec4899"
              title="AI-powered health tracking"
              description={<>Connect a wearable to sync your health data automatically.<br />Track sleep, recovery, activity, and more with AI insights.</>}
            />
            {renderWearableButtons(skipWearable)}
          </>
        ) : (
          <DailyLogView healthStats={allItems} onSyncWhoop={handleSyncWearable} onReconnectWhoop={() => setShowSwitchModal(true)} syncing={syncing} connecting={!!connecting} whoopConnected={!!activeWearable} onStatsChanged={() => queryClient.invalidateQueries({ queryKey: [...queryKeys.objects.byType('stat'), 'health'] })} onSilentSendMessage={handleSilentSendMessage} onOpenLogModal={() => setShowAddModal(true)} onSaveVitals={async (data) => {
            try {
              const today = new Date().toISOString().slice(0, 10);
              if (data.weight) {
                await api.objects.createObject(token ?? null, {
                  type: 'stat', label: 'Weight', value: data.weight, unit: 'lbs',
                  date: `${today}T00:00:00.000Z`, source: 'health:vitals', category: 'health',
                  metricKey: `health:vitals:weight:${today}`,
                  moduleLinks: [{ moduleId: module.id }],
                } as any);
              }
              if (data.systolic && data.diastolic) {
                await api.objects.createObject(token ?? null, {
                  type: 'stat', label: 'Blood Pressure', value: `${data.systolic}/${data.diastolic}`, unit: 'mmHg',
                  date: `${today}T00:00:00.000Z`, source: 'health:vitals', category: 'health',
                  systolic: data.systolic, diastolic: data.diastolic,
                  metricKey: `health:vitals:bp:${today}`,
                  moduleLinks: [{ moduleId: module.id }],
                } as any);
              }
              queryClient.invalidateQueries({ queryKey: [...queryKeys.objects.byType('stat'), 'health'] });
              showToast(data.weight ? 'Weight logged' : 'Blood pressure logged', 'success');
            } catch { showToast('Failed to save', 'error'); }
          }} />
        ))}

        {activeTab === 'profile' && <HealthProfile />}

        {activeTab === 'labs' && <LabResultsView healthStats={allItems} onAddLabReport={() => setShowAddModal(true)} onDeleteReport={async (id) => {
            try {
              await api.objects.deleteObject(token ?? null, 'stat', id);
              queryClient.invalidateQueries({ queryKey: [...queryKeys.objects.byType('stat'), 'health'] });
              showToast('Lab report deleted', 'success');
            } catch { showToast('Failed to delete', 'error'); }
          }} />}

        {activeTab === 'trends' && (
          <>
            {!hasData && !wearableSkipped ? (
              /* Empty state */
              <>
                <ModuleEmptyState
                  icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
                  iconColor="#ec4899"
                  title="AI-powered health tracking"
                  description={<>Connect a wearable to sync your health data automatically.<br />Track sleep, recovery, activity, and more with AI insights.</>}
                />
                {renderWearableButtons(skipWearable)}
              </>
            ) : (
              <>
                {/* Gear icon — always visible so user can customize even with 0 stars */}
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Key Scores</span>
                  <div style={{ position: 'relative' }}>
                    <button ref={gearRef} onClick={() => setShowMetricsSettings(!showMetricsSettings)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-faint)' }}
                      title="Customize metrics">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </svg>
                    </button>
                    {showMetricsSettings && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, marginTop: 4 }}>
                        <MetricsSettingsPanel
                          metrics={Object.keys(chartData)}
                          starred={starredMetrics}
                          hidden={hiddenMetrics}
                          metricLabels={SOURCE_LABELS}
                          onUpdate={handleMetricsUpdate}
                          onClose={() => setShowMetricsSettings(false)}
                          anchorRef={gearRef}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Starred score cards */}
                {summaryCards.filter(c => prefsMatch(starredMetrics, c.source)).length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(summaryCards.filter(c => prefsMatch(starredMetrics, c.source)).length, 4)}, 1fr)`, gap: 10, marginBottom: 16 }}>
                    {summaryCards.filter(c => prefsMatch(starredMetrics, c.source)).map((card) => (
                      <div key={card.source} style={{
                        padding: '14px 14px 10px', borderRadius: 12, background: 'var(--color-surface)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                          {card.label}
                          {card.provider && <span style={{ fontSize: 8, fontWeight: 600, color: '#fff', background: PROVIDER_COLORS[card.provider] || '#9ca3af', borderRadius: 3, padding: '1px 4px', textTransform: 'uppercase' }}>{card.provider}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                            {typeof card.latest === 'number' ? (card.latest % 1 === 0 ? card.latest : card.latest.toFixed(1)) : card.latest}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', fontWeight: 500 }}>{card.unit}</span>
                        </div>
                        {card.trend !== 0 && (
                          <div style={{ fontSize: 11, marginTop: 4, color: card.trend > 0 ? '#22c55e' : '#ef4444', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <span>{card.trend > 0 ? '\u2191' : '\u2193'}</span>
                            <span>{Math.abs(card.trend % 1 === 0 ? card.trend : Number(card.trend.toFixed(1)))}</span>
                            <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>vs avg</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {starredMetrics.length === 0 && (
                  <div style={{ padding: '12px 0 16px', fontSize: 13, color: 'var(--color-text-faint)', textAlign: 'center' }}>
                    No starred metrics. Click the gear icon to choose up to 6.
                  </div>
                )}

                {/* Sparkline chart */}
                {/* AI Trend Analysis */}
                {Object.keys(chartData).length > 0 && (() => {
                  const insights: string[] = [];
                  const weightPts = chartData['weight'];
                  if (weightPts && weightPts.length >= 7) {
                    const sorted = [...weightPts].sort((a, b) => a.date.localeCompare(b.date));
                    const first = sorted[0].value;
                    const last = sorted[sorted.length - 1].value;
                    const diff = last - first;
                    if (Math.abs(diff) >= 0.5) {
                      insights.push(`Weight ${diff < 0 ? 'down' : 'up'} ${Math.abs(diff).toFixed(1)} lbs over ${sorted.length} days (${first.toFixed(1)} → ${last.toFixed(1)}).${diff < 0 ? ' Good progress — keep it steady.' : ' Consider reviewing calorie targets.'}`);
                    } else {
                      insights.push(`Weight stable around ${last.toFixed(1)} lbs over ${sorted.length} days.`);
                    }
                  }
                  const calPts = chartData['calories'];
                  if (calPts && calPts.length >= 7) {
                    // Get user's calorie target from profile stats
                    const profileStat = allItems.find((s: any) => (s.source || s.attributes?.source) === 'health:profile');
                    const calTarget = profileStat?.dailyCalorieTarget || profileStat?.calorieTarget || profileStat?.attributes?.dailyCalorieTarget || profileStat?.attributes?.calorieTarget || 1800;
                    const avg = Math.round(calPts.reduce((s, p) => s + p.value, 0) / calPts.length);
                    const overDays = calPts.filter(p => p.value > calTarget).length;
                    insights.push(`Averaging ${avg.toLocaleString()} cal/day (target: ${calTarget.toLocaleString()}). ${overDays > calPts.length * 0.5 ? `Over target ${overDays} of ${calPts.length} days — watch portion sizes.` : `On target most days — nice discipline.`}`);
                  }
                  const exPts = chartData['exercise'];
                  if (exPts && exPts.length >= 3) {
                    const activeDays = exPts.filter(p => p.value > 0).length;
                    const avgBurn = Math.round(exPts.reduce((s, p) => s + p.value, 0) / exPts.length);
                    insights.push(`Active ${activeDays} of the last 30 days, averaging ${avgBurn} cal burned per session.${activeDays >= 20 ? ' Excellent consistency.' : activeDays >= 12 ? ' Solid routine.' : ' Try to add 1-2 more sessions per week.'}`);
                  }
                  const bpPts = chartData['systolic'];
                  if (bpPts && bpPts.length >= 7) {
                    const avgSys = Math.round(bpPts.reduce((s, p) => s + p.value, 0) / bpPts.length);
                    insights.push(`Average systolic BP: ${avgSys} mmHg.${avgSys < 120 ? ' Optimal range.' : avgSys < 130 ? ' Elevated — monitor closely.' : ' High — consult your doctor.'}`);
                  }
                  if (insights.length === 0) return null;
                  return (
                    <div style={{
                      padding: '12px 16px', marginBottom: 12,
                      borderLeft: '3px solid #0ea5e9',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                        30-Day Trend Analysis
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {insights.map((text, i) => (
                          <div key={i} style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>{text}</div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {Object.keys(chartData).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {(() => {
                      const CHART_ORDER = ['weight', 'calories', 'protein', 'exercise', 'systolic', 'diastolic', 'whoop/recovery', 'whoop/strain', 'whoop/sleep', 'whoop/workout'];
                      return Object.entries(chartData)
                        .filter(([key, pts]) => pts.length >= 3 && !prefsMatch(hiddenMetrics, key))
                        .sort(([a], [b]) => {
                          // Starred metrics first
                          const aStarred = prefsMatch(starredMetrics, a) ? 0 : 1;
                          const bStarred = prefsMatch(starredMetrics, b) ? 0 : 1;
                          if (aStarred !== bStarred) return aStarred - bStarred;
                          const ai = CHART_ORDER.indexOf(a);
                          const bi = CHART_ORDER.indexOf(b);
                          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                        });
                    })().map(([source, points]) => {
                      const color = getSourceColor(source);
                      const m = metricOf(source);
                      const prov = providerOf(source);
                      const label = SOURCE_LABELS[m] || SOURCE_LABELS[source] || source;
                      const unitMap: Record<string, string> = { calories: 'cal', protein: 'g', exercise: 'cal', weight: 'lbs', systolic: 'mmHg', diastolic: 'mmHg' };
                      return (
                        <div key={source} style={{ padding: '14px 14px 8px', borderRadius: 12, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color.fg, display: 'inline-block' }} />
                            {label}
                            {prov && <span style={{ fontSize: 9, fontWeight: 500, color: '#fff', background: PROVIDER_COLORS[prov] || '#9ca3af', borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{prov}</span>}
                          </div>
                          <Sparkline data={points} color={color.fg} height={90} unit={unitMap[m] || unitMap[source]} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* All Metrics — below charts */}
                {summaryCards.filter(c => !prefsMatch(starredMetrics, c.source) && !prefsMatch(hiddenMetrics, c.source)).length > 0 && (
                  <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>All Metrics</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                    {summaryCards.filter(c => !prefsMatch(starredMetrics, c.source) && !prefsMatch(hiddenMetrics, c.source)).map((card) => (
                      <div key={card.source} style={{
                        padding: '10px 10px 8px', borderRadius: 10, background: 'var(--color-surface)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)', overflow: 'hidden',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {card.label}
                          {card.provider && <span style={{ fontSize: 7, fontWeight: 600, color: '#fff', background: PROVIDER_COLORS[card.provider] || '#9ca3af', borderRadius: 3, padding: '1px 3px', textTransform: 'uppercase', flexShrink: 0 }}>{card.provider}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                            {typeof card.latest === 'number' ? (card.latest % 1 === 0 ? card.latest : card.latest.toFixed(1)) : card.latest}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontWeight: 500 }}>{card.unit}</span>
                        </div>
                        {card.trend !== 0 && (
                          <div style={{ fontSize: 10, marginTop: 2, color: card.trend > 0 ? '#22c55e' : '#ef4444', fontWeight: 500 }}>
                            {card.trend > 0 ? '\u2191' : '\u2193'}{Math.abs(card.trend % 1 === 0 ? card.trend : Number(card.trend.toFixed(1)))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  </>
                )}

              </>
            )}
          </>
        )}
      </div>

      {/* Switch wearable modal */}
      {showSwitchModal && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
          }}
          onClick={() => setShowSwitchModal(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--color-surface)', borderRadius: 12, padding: '20px 24px', width: 320,
              maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              fontFamily: "'DM Sans', sans-serif",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 4 }}>
              {activeWearable ? 'Switch wearable' : 'Connect a wearable'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 14 }}>
              {activeWearable
                ? `Currently connected to ${activeWearable.provider.charAt(0).toUpperCase() + activeWearable.provider.slice(1)}. Only one wearable can be active at a time.`
                : 'Choose a wearable to sync your health data.'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {WEARABLE_OPTIONS.map((w) => {
                const isActive = activeWearable?.provider === w.provider;
                const isConnecting = connecting === w.provider;
                return (
                  <button
                    key={w.provider}
                    onClick={() => {
                      if (isActive) return;
                      handleConnectProvider(w.provider, w.name);
                      setShowSwitchModal(false);
                    }}
                    disabled={isActive || !!connecting}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      borderRadius: 8, border: isActive ? `2px solid ${w.color}` : '1px solid var(--color-border, #DADCE0)',
                      background: isActive ? `${w.color}08` : 'transparent',
                      cursor: isActive ? 'default' : connecting ? 'wait' : 'pointer',
                      fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
                      opacity: (!!connecting && !isConnecting) ? 0.5 : 1,
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 7, background: w.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {w.logo}
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{w.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{w.desc}</div>
                    </div>
                    {isActive && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,0.08)',
                        padding: '2px 8px', borderRadius: 4,
                      }}>
                        Connected
                      </span>
                    )}
                    {isConnecting && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Connecting...</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
              {activeWearable ? (
                <button
                  onClick={() => { handleDisconnectWearable(); setShowSwitchModal(false); }}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                    border: '1px solid #fecaca', background: 'transparent',
                    color: '#ef4444', cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Disconnect
                </button>
              ) : <div />}
              <button
                onClick={() => setShowSwitchModal(false)}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                  border: '1px solid var(--color-border, #DADCE0)', background: 'transparent',
                  color: 'var(--color-text-secondary)', cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <style>{`
        @keyframes health-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {showAddModal && (
        <AddMealModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddEntry}
          submitting={addSubmitting}
          mode={activeTab === 'labs' ? 'labs' : activeTab === 'profile' ? 'profile' : 'log'}
        />
      )}

      {showObjectives && createPortal(
        <div
          onClick={() => setShowObjectives(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '15vh', zIndex: 10000, fontFamily: "'DM Sans', sans-serif" }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--color-surface)', borderRadius: 16, padding: 20, width: 380, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)' }}>Set Objectives</span>
              <button onClick={() => setShowObjectives(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', fontSize: 18, padding: '2px 4px' }}>&#x2715;</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Daily Calorie Target</label>
                <input
                  type="number"
                  value={objectiveCalories}
                  onChange={e => setObjectiveCalories(e.target.value)}
                  placeholder="e.g. 1800"
                  style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#1f2937', marginTop: 4, boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Daily Protein Target (g)</label>
                <input
                  type="number"
                  value={objectiveProtein}
                  onChange={e => setObjectiveProtein(e.target.value)}
                  placeholder="e.g. 120"
                  style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#1f2937', marginTop: 4, boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleAIObjectives}
                style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid #e5e7eb', background: 'var(--color-surface)', color: 'var(--color-text-faint)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
              >
                Let AI decide
              </button>
              <button
                onClick={handleSaveObjectives}
                disabled={!objectiveCalories && !objectiveProtein}
                style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', background: (objectiveCalories || objectiveProtein) ? '#0ea5e9' : '#e5e7eb', color: (objectiveCalories || objectiveProtein) ? '#fff' : '#9ca3af', fontSize: 13, fontWeight: 600, cursor: (objectiveCalories || objectiveProtein) ? 'pointer' : 'default', fontFamily: "'DM Sans', sans-serif" }}
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function formatHealthDate(dateStr: string): string {
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}
